import CtaBannerV2 from "@/components/Home/v2/CtaBannerV2";
import FaqV2 from "@/components/Home/v2/FaqV2";
import StoriesMarqueeV2 from "@/components/Home/v2/StoriesMarqueeV2";
import AccordionWithMediaSection from "@/components/Home/v2/AccordionWithMediaSection";
import AiTeamSection from "@/components/Home/v2/AiTeamSection";
import ProductCardGridSection from "@/components/Product/v2/ProductCardGridSection";
import CtaButtonGroup, { CtaActionType, CtaType } from "@/components/molecules/CtaButtonGroup";
import Text from "@/components/Home/v2/shared/Text";
import { DEFAULT_CALENDLY_LINK } from "@/shared/constants/calendly.constants";
import { lmsV2Data, LmsHeroProps } from "@/shared/data/lms.v2.data";
import { ButtonSizeEnum, ButtonTypeEnum } from "@/components/atoms/Button";
import { faBookOpen } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Image from "next/image";
import Link from "next/link";

/**
 * /page/learning-management-system
 *
 * Eight sections:
 *   1. Hero — LMS eyebrow, headline, subtitle, CTAs, metric pills, mockup
 *   2. AccordionWithMediaSection (image-right) — "Create Learning Experiences"
 *   3. IdeaToIncomeSection (3-up) — "Sell Globally. Payments Built…"
 *   4. AccordionWithMediaSection (image-left) — "Profitable Marketing Funnels"
 *   5. AiTeamSection (dark) — "Your AI Team that Builds, Sells & Scales"
 *   6. StoriesMarqueeV2 — "Creators Who Sell More, Without Doing More"
 *   7. FaqV2 — Frequently Asked Questions
 *   8. CtaBannerV2 — "Ready to Build Your Creator Business?"
 *
 * Reuses the minimal sticky header from the /page/*-alternative pages
 * (GRAPHY logo + Book A Demo pill), no footer.
 */

/* ── Minimal header (shared pattern with /page/*-alternative) ───── */

const LmsHeader = () => (
  <header className="sticky top-0 z-bar w-full border-b border-transparent bg-white/60 font-inter backdrop-blur-md">
    <div className="container mx-auto px-4 py-4 sm:px-6 md:px-8 lg:px-12">
      <div className="mx-auto flex max-w-6xl items-center justify-between">
        <Link href="/" aria-label="Graphy home">
          <Image
            src="/images/graphy_logo_black.svg"
            width={110}
            height={32}
            alt="Graphy"
            priority
            className="h-7 w-auto md:h-8"
          />
        </Link>
        <Link
          href={DEFAULT_CALENDLY_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center rounded-full bg-brandPrimary px-5 py-2.5 font-inter text-[14px] font-medium text-white transition-opacity hover:opacity-90 md:text-[15px]"
        >
          Book a Demo
        </Link>
      </div>
    </div>
  </header>
);

/* ── Section 1 · Hero ───────────────────────────────────────────── */

const HeroEyebrow = ({ label }: { label: string }) => (
  <div className="flex items-center justify-center gap-2">
    <FontAwesomeIcon
      icon={faBookOpen}
      className="text-[14px] text-[#B3741A]"
      aria-hidden="true"
    />
    <span
      className="font-inter text-[13px] font-semibold uppercase text-[#B3741A]"
      style={{ letterSpacing: "0.6px" }}
    >
      {label}
    </span>
  </div>
);

const Hero = ({
  eyebrow,
  titleMuted,
  titleBright,
  subtitle,
  ctas,
  image,
  imageMobile,
}: LmsHeroProps) => (
  <section
    className="relative w-full overflow-hidden"
    style={{
      background:
        "radial-gradient(120% 90% at 50% 110%, rgba(99, 102, 241, 0.22) 0%, rgba(167, 181, 255, 0.18) 35%, rgba(220, 230, 255, 0.12) 60%, rgba(255, 255, 255, 1) 100%)",
    }}
  >
    <div className="container mx-auto px-4 pb-0 pt-10 sm:px-6 md:px-8 md:pt-14 lg:px-12 lg:pt-20">
      <div className="mx-auto max-w-4xl text-center">
        <HeroEyebrow label={eyebrow} />
        <Text
          as="h1"
          variant="display-lg"
          className="mt-3 text-[28px] text-neutralBlack sm:text-[32px] md:mt-4 md:text-[44px] lg:text-[56px]"
        >
          <span>{titleMuted}</span>
          <br />
          <span>{titleBright}</span>
        </Text>
        <Text
          as="p"
          variant="body-lg"
          className="mx-auto mt-5 max-w-2xl text-darkSlate04"
          style={{ letterSpacing: "-0.08px" }}
        >
          {subtitle}
        </Text>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <CtaButtonGroup
            ctas={ctas}
            gap="flex gap-3"
            fullWidthOnMobile={false}
          />
        </div>
      </div>

      <div className="mx-auto mt-10 block max-w-5xl md:mt-14">
        {image ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={image}
            alt=""
            aria-hidden="true"
            className={
              imageMobile
                ? "hidden h-auto w-full md:block"
                : "block h-auto w-full"
            }
          />
        ) : null}
        {imageMobile ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={imageMobile}
            alt=""
            aria-hidden="true"
            className={
              image ? "block h-auto w-full md:hidden" : "block h-auto w-full"
            }
          />
        ) : null}
      </div>
    </div>
  </section>
);

const lmsSignupCta: CtaType = {
  type: ButtonTypeEnum.Secondary,
  size: ButtonSizeEnum.Regular,
  text: "Start Your Free Trial",
  actionType: CtaActionType.DelayedHref,
  href: "https://graphy.com/signup/",
};

const overrideCtas = (ctas: CtaType[]) =>
  ctas.map((c) => (c.actionType === CtaActionType.SignUp ? lmsSignupCta : c));

/* ── Page orchestrator ───────────────────────────────────────────── */

const LearningManagementSystem = ({ locale }: { locale?: string }) => {
  const isIndia = locale === "in";
  const heroCtas = isIndia ? overrideCtas(lmsV2Data.hero.ctas) : lmsV2Data.hero.ctas;
  const storiesCtas = isIndia ? overrideCtas(lmsV2Data.storiesCtas) : lmsV2Data.storiesCtas;
  const faqCtas = isIndia ? overrideCtas(lmsV2Data.faqCtas) : lmsV2Data.faqCtas;
  const sellGlobally = isIndia
    ? { ...lmsV2Data.sellGlobally, ctas: overrideCtas(lmsV2Data.sellGlobally.ctas ?? []) }
    : lmsV2Data.sellGlobally;
  const aiTeam = isIndia
    ? { ...lmsV2Data.aiTeam, ctas: overrideCtas(lmsV2Data.aiTeam.ctas ?? []) }
    : lmsV2Data.aiTeam;
  const ctaBanner = isIndia
    ? { ...lmsV2Data.ctaBanner, ctas: overrideCtas(lmsV2Data.ctaBanner.ctas) }
    : lmsV2Data.ctaBanner;

  return (
    <div className="home-v2-scope relative">
      <LmsHeader />
      <main className="relative bg-white">
        <Hero {...lmsV2Data.hero} ctas={heroCtas} />
        <AccordionWithMediaSection {...lmsV2Data.learningExperiences} />
        <ProductCardGridSection {...sellGlobally} />
        <AccordionWithMediaSection {...lmsV2Data.marketingFunnels} />
        <AiTeamSection {...aiTeam} />
        <StoriesMarqueeV2
          eyebrow="STORIES"
          titleMuted="Creators Who Sell More,"
          titleBright="Without Doing More"
          subtitle="Thousands of creators use Graphy to automate selling, deliver better experiences, and grow their course businesses globally."
          ctas={storiesCtas}
          stories={lmsV2Data.stories}
        />
        <FaqV2
          title="Frequently Asked Questions"
          ctas={faqCtas}
          items={lmsV2Data.faqItems}
        />
        <CtaBannerV2 {...ctaBanner} />
      </main>
    </div>
  );
};

export default LearningManagementSystem;
